import { useContext, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useBreakpoints } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useRect } from 'react-use-rect';
import { classnamePrefix, TABLET_WIDTH } from '../../../../constants';
import { processSource } from '../../../helpers';
import { sourceContext } from '../../../SourceContext';
import { Header } from '../../common/header';
import { currentVideoContext } from '../../currentVideoContext';
import { Player } from '../../videos/video';
import { Info } from '../../videos/video/info';
import { PlaylistVideos } from './PlaylistVideos';

export const Playlist = ({
  children,
  hasNext = false,
  loadNext = () => {},
  loading = false,
  nextVideo = () => {}
}) => {
  const { playMode, headerVisible, playerElements } = useSettings();
  const { currentVideo } = useContext(currentVideoContext);
  const [height, setHeight] = useState(0);
  const [breakpoints, ref] = useBreakpoints(TABLET_WIDTH);
  const videos = useRef(null);
  const { videosSource } = useContext(sourceContext);
  const [{ type }] = useState(processSource({ source: videosSource }));

  const [playerRef] = useRect(({ height }) => setHeight(height), {
    resize: true
  });

  const isTablet = breakpoints[TABLET_WIDTH];
  const isPlayer = playMode === 'player';

  return (
    <Component ref={ref}>
      {headerVisible && type !== 'videos' && <Header />}

      <Container _isTablet={isTablet}>
        {playMode === 'player' && currentVideo && (
          <Video>
            <PlayerWrapper ref={playerRef}>
              <Player item={currentVideo} nextVideo={nextVideo} />
            </PlayerWrapper>
          </Video>
        )}

        <Videos
          ref={videos}
          _isTablet={isTablet}
          _isPlayer={isPlayer}
          _height={height}
        >
          <PlaylistVideos
            loading={loading}
            loadNext={loadNext}
            hasNext={hasNext}
          >
            {children}
          </PlaylistVideos>
        </Videos>
      </Container>

      {playMode === 'player' && currentVideo && (
        <Info data={currentVideo} elements={playerElements} />
      )}
    </Component>
  );
};

const Component = styled.div.attrs({
  className: `${classnamePrefix}-playlist-component`
})`
  display: flex;
  overflow: hidden;
  align-items: flex-start;
  flex-grow: 1;
  flex-flow: column wrap;
`;

const Container = styled.div.attrs({
  className: `${classnamePrefix}-playlist-container`
})`
  display: flex;
  overflow: hidden;
  align-items: flex-start;
  flex-grow: 1;
  width: 100%;

  ${({ _isTablet }) =>
    _isTablet &&
    css`
      flex-direction: column;
    `}
`;

const Video = styled.div.attrs({
  className: `${classnamePrefix}-playlist-video`
})`
  width: 100%;
`;

const PlayerWrapper = styled.div.attrs({
  className: `${classnamePrefix}-playlist-player`
})`
  display: flex;
`;

const Videos = styled.div.attrs({
  className: `${classnamePrefix}-playlist-videos`
})`
  width: 320px;
  flex-shrink: 0;

  ${({ _isPlayer, _isTablet, _height }) => css`
    height: ${_isPlayer && !_isTablet ? `${_height}px` : '264px'};
    ${!_isPlayer && 'width: 100%'};
    ${!_isPlayer && 'max-height: 600px'};
    ${_isTablet && 'width: 100%'};
  `}
`;
